import React, { useState } from 'react';
import BarChart from '../../components/charts/BarChart';
import StatCard from '../../components/charts/StatCard';
import Button from '../../components/common/Button';
import { useEffect } from 'react';
import api from '../../services/api';

const ManagerReportsView = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('This Month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [ticketCategory, setTicketCategory] = useState('all');
  const [metrics, setMetrics] = useState({
    sla_compliance: '0%',
    customer_satisfaction: '0.0/10',
    engineer_efficiency: '0%',
    sla_trend: [],
    resolution_trend: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      if (selectedPeriod === 'Custom' && (!startDate || !endDate)) {
        return;
      }
      setLoading(true);
      try {
        const response = await api.get('/api/v1/reports/metrics', {
          params: { 
            period: selectedPeriod,
            start_date: selectedPeriod === 'Custom' ? startDate : undefined,
            end_date: selectedPeriod === 'Custom' ? endDate : undefined,
            ticket_category: ticketCategory
          }
        });
        setMetrics(response.data);
      } catch (error) {
        console.error('Failed to fetch report metrics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, [selectedPeriod, startDate, endDate, ticketCategory]);

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const chartGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const filterBarStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap'
  };

  const selectStyle = {
    padding: '10px 16px',
    fontSize: '14px',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    background: 'var(--surface)',
    color: 'var(--text-primary)',
    outline: 'none',
    fontFamily: 'inherit',
    fontWeight: '600'
  };

  const reportCardStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    transition: 'all 0.3s ease',
    cursor: 'pointer'
  };

  const reportTypes = [
    {
      id: 'sla',
      name: 'SLA Performance Report',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#10b981' }}>
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
      description: 'Service level agreement compliance'
    },
    {
      id: 'engineer',
      name: 'Engineer Performance',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#8b5cf6' }}>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
      description: 'Individual and team metrics'
    },
    {
      id: 'customer',
      name: 'Customer Satisfaction',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#f59e0b' }}>
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ),
      description: 'Feedback and ratings'
    },
    {
      id: 'ticket',
      name: 'Ticket Analytics',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#3b82f6' }}>
          <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2z" />
          <line x1="13" y1="5" x2="13" y2="19" strokeDasharray="4" />
        </svg>
      ),
      description: 'Ticket trends and patterns'
    },
    {
      id: 'contract',
      name: 'Contract Overview',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#06b6d4' }}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      ),
      description: 'Active and expiring contracts'
    }
  ];

  const handleDownloadReport = async (reportType) => {
    try {
      const response = await api.get(`/api/v1/reports/${reportType}/download`, {
        params: { 
          period: selectedPeriod,
          start_date: selectedPeriod === 'Custom' ? startDate : undefined,
          end_date: selectedPeriod === 'Custom' ? endDate : undefined,
          ticket_category: ticketCategory
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}-report-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Error downloading report:', downloadError);
    }
  };

  return (
    <div>
      <div style={filterBarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
            Period:
          </span>
          <select
            style={selectStyle}
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            <option>Today</option>
            <option>This Week</option>
            <option>This Month</option>
            <option>Last Month</option>
            <option>This Quarter</option>
            <option>This Year</option>
            <option>Custom</option>
          </select>

          <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginLeft: '12px' }}>
            Ticket Types:
          </span>
          <select
            style={selectStyle}
            value={ticketCategory}
            onChange={(e) => setTicketCategory(e.target.value)}
          >
            <option value="all">All Ticket Types</option>
            <option value="amc_complaint">AMC Complaint and Project warranty Only</option>
            <option value="pm_ppm">PPM and PRJ PPM Warranty only</option>
          </select>

          {selectedPeriod === 'Custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="date"
                style={{
                  ...selectStyle,
                  padding: '8px 12px',
                  fontWeight: 'normal'
                }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>to</span>
              <input
                type="date"
                style={{
                  ...selectStyle,
                  padding: '8px 12px',
                  fontWeight: 'normal'
                }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      <div style={gridStyle}>
        <StatCard
          title="SLA Compliance"
          value={loading ? "..." : metrics.sla_compliance}
          icon=""
          subtitle="During selected period"
          color="#10b981"
        />
        <StatCard
          title="Avg Resolution Time"
          value={loading ? "..." : (metrics.avg_resolution_time || 'N/A')}
          icon=""
          subtitle="Time to resolve"
          color="#3b82f6"
        />
        <StatCard
          title="Customer Satisfaction"
          value={loading ? "..." : metrics.customer_satisfaction}
          icon=""
          subtitle="Average rating"
          color="#f59e0b"
        />
        <StatCard
          title="Engineer Efficiency"
          value={loading ? "..." : metrics.engineer_efficiency}
          icon=""
          subtitle="Tickets closed vs assigned"
          color="#8b5cf6"
        />
      </div>

      <div style={chartGridStyle}>
        <BarChart
          data={metrics.sla_trend}
          title="SLA Performance Trend (Last 6 Months)"
          xKey="month"
          yKey="met"
          color="#10b981"
          height={300}
        />
        <BarChart
          data={metrics.resolution_trend}
          title="Ticket Resolution Rate (This Week)"
          xKey="day"
          yKey="resolved"
          color="#3b82f6"
          height={300}
        />
      </div>

      <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '20px' }}>
        Available Reports
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '16px' }}>
        {reportTypes.map((report) => (
          <div
            key={report.id}
            style={reportCardStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => handleDownloadReport(report.id)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                fontSize: '36px',
                width: '60px',
                height: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--surface-light)',
                borderRadius: '12px'
              }}>
                {report.icon}
              </div>
              <div>
                <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '4px' }}>
                  {report.name}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {report.description}
                </div>
              </div>
            </div>
            <Button variant="secondary" size="small">
               Download
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ManagerReportsView;
